import { ReadLsDataV2Response } from "@api/stagingarea-api/stagingarea-api-types";
import { ISOTimeString, UserId } from "@faro-lotv/ielement-types";
import { CaptureTreeEntityType } from "@faro-lotv/service-wires";
import { UUID } from "@stellar/api-logic";
import { ZipFile } from "@utils/zip-file";
import { UploadElementType, PhotogrammetryUploadTaskContext } from "@custom-types/file-upload-types";
import { SettingsApiTypes } from "@stellar/settings-api-client";

// eslint-disable-next-line @typescript-eslint/naming-convention -- Default value
export const DEFAULT_SETTING_PC = true;
// eslint-disable-next-line @typescript-eslint/naming-convention -- Default value
export const DEFAULT_SETTING_REFLECTION = false;

// publish opts labels in CoreApi
export const SETTING_PC = "processing_projectpointcloud";
export const SETTING_REFLECTION = "processing_reflectionfilter";

// Response types from the SettingsApi for each publish opt in the Data Management
export type SettingsResponsePC = SettingsApiTypes.SettingsResponse<"PROJECT", typeof SETTING_PC>;
export type SettingsResponseReflection = SettingsApiTypes.SettingsResponse<"PROJECT", typeof SETTING_REFLECTION>;

/** If there's no error, "uploaded" and "processed" are usually jumped over for the next state. */
export type WorkflowState = "start" | "startError" |
  "uploading"   | "uploaded" | "uploadError" |
  "processing"  | "processed" | "processError" |
  "registering" | "registered" | "registerError" | "registerBadResult" |
  "publishing" | "published" | "publishError";

/** Subset of WorkflowState with only error states (incl. bad registration). */
export type WorkflowStateError = "startError" | "uploadError" | "processError" |
  "registerBadResult" | "registerError" | "publishError";

/** @returns True if `state` is an error state (incl. bad registration). */
export function isWorkflowStateError(state: WorkflowState): state is WorkflowStateError {
  return state === "startError" || state === "uploadError" || state === "processError" ||
    state === "registerBadResult" || state === "registerError" || state === "publishError";
}

export type StepState = "todo" | "active" | "done" | "error";

export type TableItemState = "start" | "uploading" | "uploaded" | "processing" | "unpublished" | "published" | "error";

/** States in which the name of the scan should be editable */
export const allowEditingStates: TableItemState[] = [
  "uploaded", "processing", "unpublished", "published",
];

/** Indices for the Staging Area stepper. */
export enum StepperIndices {
  upload = 0,
  process = 1,
  register = 2,
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  publish = 3,
}

/** Headers for the Staging Area table. */
export enum TableHeaders {
  /** File name or other user-friendly name. */
  name = "Name",
  /** User who created the entity. */
  createdBy = "Created by",
  /** The date time when the entity was created. */
  createdAt = "Recorded at",
  /** Type of device used to generate the entity. */
  deviceType = "Device",
  /** Status icon such as the processing spinner. */
  status = "Status",
  /** Action buttons. */
  actions = "Actions",
}

/** Interface for the scan entities shown in the Staging Area table. */
export interface TableItem {
  /** Entity ID. */
  id: string;
  /** Type of the entity. */
  type: "Upload" | "Scan";
  /** Scan name or other user-friendly name. */
  name: string;
  /** Additional name, e.g. the file name, for the tooltip. */
  additionalName?: string;
  /** User who created the entity. */
  createdBy: UserId;
  /** The date time when the entity was created. */
  createdAt: ISOTimeString;
  /** Type of device used to generate the entity. */
  deviceType: CaptureTreeEntityType;
  /** State of the entity. */
  state: TableItemState;
  /** Progress from 0 to 100. Currently not displayed. */
  progress?: number;
};

/** Set of UUIDs, implemented as a Record with value `true`. */
export type UploadedIdsMap = { [key: UUID]: boolean };

export enum CheckFilesErrorToastType {
  tooLongFilename = "tooLongFilename",
  tooLargeScans = "tooLargeScans",
  tooLargePhotos = "tooLargePhotos",
  collectFiles = "collectFiles",
  noFiles = "noFiles",
  noMultiFolder = "noMultiFolder",
  duplicateFiles = "duplicateFiles",
  invalidFormat = "invalidFormat",
  invalidMetadata = "invalidMetadata",
  noNewFiles = "noNewFiles",
  noPhotogrammetryAccess = "noPhotogrammetryAccess",
  noFocusAccess = "noFocusAccess",
  noOrbisAccess = "noOrbisAccess",
}

export interface FilesInfo {
  type: UploadElementType,
  /** Name of the folder or dataset. */
  name: string | undefined,
  /**
   * Files to upload with (excluding metadata files which aren't uploaded in the regular way).
   * Supporting Files and ZipFiles. Using union type to allow both in one array.
   */
  filesToUpload: (File | ZipFile)[],
  /** Error, if the dataset was detected (e.g. as Blink folder), but is invalid. */
  errorType?: CheckFilesErrorToastType,
}

export interface InvalidFilesInfo extends FilesInfo {
  errorType: CheckFilesErrorToastType,
}

/** Data required by useOnUploadBlinkFiles and provided by useOnCheckFiles / getBlinkFilesInfo. */
export interface BlinkFilesInfo extends FilesInfo {
  type: UploadElementType.elsScan,
  lsDataV2: ReadLsDataV2Response,
  lsDataV2ZipFile?: ZipFile,
  scansAlreadyUploaded: Set<File>,
}

/** One or more Orbis datasets. Files are of the type .geoslam */
export interface OrbisFilesInfo extends FilesInfo {
  type: UploadElementType.orbisScan,
  lsDataV2: ReadLsDataV2Response,
  lsDataV2ZipFile?: ZipFile,
  scansAlreadyUploaded: Set<File>,
  hasDummyLsDataV2: boolean,
}

/** Set of Focus scans. No edges or poses are saved when using this FilesInfo. */
export interface FocusFilesInfo extends FilesInfo {
  type: UploadElementType.focusScan
  /** In this case lsDataV2 is created from the limited metadata that is available in FocusRaw scans. */
  lsDataV2: ReadLsDataV2Response,
}

/** Data required by useOnUploadPhotogrammetryFiles and provided by useOnCheckFiles / getPhotogrammetryFilesInfo. */
export interface PhotogrammetryFilesInfo extends FilesInfo {
  type: UploadElementType.photogrammetry,
  context: PhotogrammetryUploadTaskContext,
}

/** Combines all FileInfos that are supported by useOnUploadFilesLsDataV2. */
export type LsDataV2FilesInfo = BlinkFilesInfo | OrbisFilesInfo;

/** All known FilesInfo subtypes. */
export type AnyFilesInfo = InvalidFilesInfo | BlinkFilesInfo | FocusFilesInfo | PhotogrammetryFilesInfo | OrbisFilesInfo;

export function isInvalidFileInfo(info: FilesInfo): info is InvalidFilesInfo {
  return !!info.errorType;
}

export function isBlinkFileInfo(info: FilesInfo): info is BlinkFilesInfo {
  return "type" in info && info.type === UploadElementType.elsScan;
}

export function isOrbisFileInfo(info: FilesInfo): info is OrbisFilesInfo {
  return "type" in info && info.type === UploadElementType.orbisScan;
}

export function isFocusFileInfo(info: FilesInfo): info is FocusFilesInfo {
  return "type" in info && info.type === UploadElementType.focusScan;
}

export function isPhotogrammetryFileInfo(info: FilesInfo): info is PhotogrammetryFilesInfo {
  return "type" in info && info.type === UploadElementType.photogrammetry;
}

export function isLsDataV2FilesInfo(info: FilesInfo): info is LsDataV2FilesInfo {
  return isBlinkFileInfo(info) || isOrbisFileInfo(info);
}

/**
 * All UploadElementTypes that are supported in Data Management.
 * This type ensures compatibility with UploadElementType
 */
export type DataManagementUploadElementType = Extract<
  UploadElementType,
  | UploadElementType.elsScan
  | UploadElementType.focusScan
  | UploadElementType.orbisScan
  | UploadElementType.photogrammetry
>;
